import * as XLSX from 'xlsx';
import { format, parseISO, isValid } from 'date-fns';

// Field mapping configuration
export interface ColumnMapping {
  airtableField: string;
  esHeader: string;
  enHeader: string;
}

// Export data record type
export interface ExportRecord {
  [key: string]: string | number | null | undefined;
}

// Accounts that have inbox export enabled
export const INBOX_ENABLED_ACCOUNTS = [
  'AICOMP NORAM',
  'AICOMP RoW/RoE',
  'SOL MEDIA',
  'MALCO'
];

// Accounts that have the Product column enabled (computed from conversation)
export const PRODUCT_COLUMN_ACCOUNTS = [
  'AICOMP NORAM',
  'AICOMP RoW/RoE'
];

// Check if account has inbox export enabled
export function isInboxExportEnabled(accountName: string): boolean {
  return INBOX_ENABLED_ACCOUNTS.includes(accountName);
}

// Check if account has Product column enabled
export function isProductColumnEnabled(accountName: string): boolean {
  return PRODUCT_COLUMN_ACCOUNTS.includes(accountName);
}

// Column mappings for meetings export
export const EXPORT_COLUMNS: ColumnMapping[] = [
  { airtableField: 'Nombre', esHeader: 'Nombre', enHeader: 'Name' },
  { airtableField: 'Empresa', esHeader: 'Empresa', enHeader: 'Company' },
  { airtableField: 'Status Lead', esHeader: 'Estado Lead', enHeader: 'Lead Status' },
  { airtableField: 'Fecha Reunión', esHeader: 'Fecha Reunión', enHeader: 'Meeting Date' },
  { airtableField: 'Teléfono', esHeader: 'Teléfono', enHeader: 'Phone' },
  { airtableField: 'Email', esHeader: 'Email', enHeader: 'Email' },
  { airtableField: 'Ciutat', esHeader: 'Ciudad', enHeader: 'City' },
  { airtableField: 'Pais', esHeader: 'País', enHeader: 'Country' },
  { airtableField: 'Página Web', esHeader: 'Página Web', enHeader: 'Website' },
  { airtableField: 'Sector', esHeader: 'Sector', enHeader: 'Industry' },
  { airtableField: 'Linkedin', esHeader: 'LinkedIn', enHeader: 'LinkedIn' },
  { airtableField: 'Facturación', esHeader: 'Facturación', enHeader: 'Revenue' },
  { airtableField: 'Empleados', esHeader: 'Empleados', enHeader: 'Employees' },
  { airtableField: 'Conversación', esHeader: 'Conversación', enHeader: 'Conversation' },
  { airtableField: 'Notas', esHeader: 'Notas', enHeader: 'Notes' },
];

// Column mappings for inbox export
// Fields: Titol, Correu Lead, Telefon, Nom, Cognom, Nom Empresa, Pais, Domini, Facturacio, Linkedin, Linkedin Empresa, Data Ultim missatge conversa, Status Lead (Inbox manager), Conversa, Empleats
export const INBOX_EXPORT_COLUMNS: ColumnMapping[] = [
  { airtableField: 'Titol', esHeader: 'Título', enHeader: 'Title' },
  { airtableField: 'Correu Lead', esHeader: 'Email', enHeader: 'Email' },
  { airtableField: 'Telefon', esHeader: 'Teléfono', enHeader: 'Phone' },
  { airtableField: 'Nom', esHeader: 'Nombre', enHeader: 'First Name' },
  { airtableField: 'Cognom', esHeader: 'Apellido', enHeader: 'Last Name' },
  { airtableField: 'Nom Empresa', esHeader: 'Empresa', enHeader: 'Company' },
  { airtableField: 'Pais', esHeader: 'País', enHeader: 'Country' },
  { airtableField: 'Domini', esHeader: 'Dominio', enHeader: 'Domain' },
  { airtableField: 'Facturacio', esHeader: 'Facturación', enHeader: 'Revenue' },
  { airtableField: 'Linkedin', esHeader: 'LinkedIn', enHeader: 'LinkedIn' },
  { airtableField: 'Linkedin Empresa', esHeader: 'LinkedIn Empresa', enHeader: 'Company LinkedIn' },
  { airtableField: 'Data Ultim missatge conversa', esHeader: 'Último Mensaje', enHeader: 'Last Message' },
  { airtableField: 'Status Lead (Inbox manager)', esHeader: 'Estado Lead', enHeader: 'Lead Status' },
  { airtableField: 'Conversa', esHeader: 'Conversación', enHeader: 'Conversation' },
  { airtableField: 'Empleats', esHeader: 'Empleados', enHeader: 'Employees' },
];

// Product header for inbox export (computed column, not from Airtable)
const PRODUCT_HEADER = { esHeader: 'Producto', enHeader: 'Product' };

// Excel cell text limit (32767 characters)
const EXCEL_MAX_CELL_LENGTH = 32767;

/**
 * Truncate text to Excel's maximum cell length
 * Excel cells have a 32,767 character limit
 */
function truncateForExcel(value: string): string {
  if (value.length <= EXCEL_MAX_CELL_LENGTH) {
    return value;
  }
  // Truncate and add indicator that text was cut
  return value.substring(0, EXCEL_MAX_CELL_LENGTH - 20) + '... [TRUNCATED]';
}

/**
 * Extract product from conversation text
 * Checks for "Cubicus" first, then "SAP" (case-insensitive)
 */
function extractProductFromConversation(conversation: string | null | undefined): string {
  if (!conversation || typeof conversation !== 'string') {
    return '';
  }

  // Check for Cubicus first (case-insensitive)
  if (/cubicus/i.test(conversation)) {
    return 'Cubicus';
  }

  // Check for SAP (case-insensitive)
  if (/sap/i.test(conversation)) {
    return 'SAP';
  }

  return '';
}

/**
 * Get column headers based on language for meetings
 * Optionally includes Product column for AICOMP accounts
 */
export function getHeaders(language: 'es' | 'en', accountName?: string): string[] {
  const baseHeaders = EXPORT_COLUMNS.map(col =>
    language === 'es' ? col.esHeader : col.enHeader
  );

  // Add Product column for AICOMP accounts
  if (accountName && isProductColumnEnabled(accountName)) {
    baseHeaders.push(language === 'es' ? PRODUCT_HEADER.esHeader : PRODUCT_HEADER.enHeader);
  }

  return baseHeaders;
}

/**
 * Get column headers based on language for inbox
 * Optionally includes Product column for AICOMP accounts
 */
export function getInboxHeaders(language: 'es' | 'en', accountName?: string): string[] {
  const baseHeaders = INBOX_EXPORT_COLUMNS.map(col =>
    language === 'es' ? col.esHeader : col.enHeader
  );

  // Add Product column for AICOMP accounts
  if (accountName && isProductColumnEnabled(accountName)) {
    baseHeaders.push(language === 'es' ? PRODUCT_HEADER.esHeader : PRODUCT_HEADER.enHeader);
  }

  return baseHeaders;
}

/**
 * Format a date value for export
 */
function formatDateValue(value: string | null | undefined): string {
  if (!value) return '';
  try {
    const date = parseISO(value);
    if (isValid(date)) {
      return format(date, 'dd/MM/yyyy');
    }
  } catch {
    // Return original value if parsing fails
  }
  return value;
}

/**
 * Transform a meeting record to export format
 * Optionally includes Product column for AICOMP accounts
 */
export function transformRecordForExport(
  fields: Record<string, unknown>,
  language: 'es' | 'en',
  accountName?: string
): Record<string, string | number> {
  const row: Record<string, string | number> = {};

  EXPORT_COLUMNS.forEach(col => {
    const header = language === 'es' ? col.esHeader : col.enHeader;
    let value = fields[col.airtableField];

    // Handle date fields
    if (col.airtableField === 'Fecha Reunión' && typeof value === 'string') {
      value = formatDateValue(value);
    }

    // Handle arrays (linked records)
    if (Array.isArray(value)) {
      value = value.join(', ');
    }

    // Handle null/undefined
    if (value === null || value === undefined) {
      value = '';
    }

    // Convert to string or number, truncating strings to Excel limit
    if (typeof value === 'number') {
      row[header] = value;
    } else {
      const stringValue = String(value);
      row[header] = truncateForExcel(stringValue);
    }
  });

  // Add computed Product column for AICOMP accounts
  if (accountName && isProductColumnEnabled(accountName)) {
    const productHeader = language === 'es' ? PRODUCT_HEADER.esHeader : PRODUCT_HEADER.enHeader;
    const conversation = fields['Conversación'];
    row[productHeader] = extractProductFromConversation(conversation as string | null | undefined);
  }

  return row;
}

/**
 * Transform an inbox record to export format
 * Optionally includes Product column for AICOMP accounts
 */
export function transformInboxRecordForExport(
  fields: Record<string, unknown>,
  language: 'es' | 'en',
  accountName?: string
): Record<string, string | number> {
  const row: Record<string, string | number> = {};

  INBOX_EXPORT_COLUMNS.forEach(col => {
    const header = language === 'es' ? col.esHeader : col.enHeader;
    let value = fields[col.airtableField];

    // Handle date fields
    if (col.airtableField === 'Data Ultim missatge conversa' && typeof value === 'string') {
      value = formatDateValue(value);
    }

    // Handle arrays (linked records)
    if (Array.isArray(value)) {
      value = value.join(', ');
    }

    // Handle null/undefined
    if (value === null || value === undefined) {
      value = '';
    }

    // Convert to string or number, truncating strings to Excel limit
    if (typeof value === 'number') {
      row[header] = value;
    } else {
      const stringValue = String(value);
      row[header] = truncateForExcel(stringValue);
    }
  });

  // Add computed Product column for AICOMP accounts
  if (accountName && isProductColumnEnabled(accountName)) {
    const productHeader = language === 'es' ? PRODUCT_HEADER.esHeader : PRODUCT_HEADER.enHeader;
    const conversation = fields['Conversa'];
    row[productHeader] = extractProductFromConversation(conversation as string | null | undefined);
  }

  return row;
}

/**
 * Escape a value for CSV format
 */
function escapeCSVValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // Check if value needs quoting (contains comma, quote, or newline)
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    // Escape quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Generate CSV content from data
 */
export function generateCSV(
  data: Record<string, unknown>[],
  language: 'es' | 'en',
  accountName?: string
): string {
  if (data.length === 0) {
    return '';
  }

  const headers = getHeaders(language, accountName);
  const rows: string[] = [];

  // Add BOM for Excel compatibility with UTF-8
  const BOM = '\uFEFF';

  // Add header row
  rows.push(headers.map(escapeCSVValue).join(','));

  // Add data rows
  data.forEach(record => {
    const fields = record.fields as Record<string, unknown>;
    const transformedRow = transformRecordForExport(fields, language, accountName);
    const rowValues = headers.map(header => escapeCSVValue(transformedRow[header]));
    rows.push(rowValues.join(','));
  });

  return BOM + rows.join('\r\n');
}

/**
 * Generate XLSX workbook from meetings data only
 */
export function generateXLSX(
  data: Record<string, unknown>[],
  language: 'es' | 'en',
  accountName?: string
): Blob {
  const headers = getHeaders(language, accountName);

  // Transform data to array of arrays
  const worksheetData: (string | number)[][] = [];

  // Add headers
  worksheetData.push(headers);

  // Add data rows
  data.forEach(record => {
    const fields = record.fields as Record<string, unknown>;
    const transformedRow = transformRecordForExport(fields, language, accountName);
    const rowValues = headers.map(header => transformedRow[header]);
    worksheetData.push(rowValues);
  });

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  const colWidths = headers.map(header => ({
    wch: Math.max(header.length, 15) // Minimum width of 15 characters
  }));
  worksheet['!cols'] = colWidths;

  // Add worksheet to workbook
  const sheetName = language === 'es' ? 'Reuniones' : 'Meetings';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate buffer
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });

  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}

/**
 * Generate XLSX workbook with meetings and inbox sheets
 * Only includes sheets that have data
 */
export function generateXLSXWithInbox(
  meetingsData: Record<string, unknown>[],
  inboxData: Record<string, unknown>[],
  language: 'es' | 'en',
  accountName: string
): Blob {
  const workbook = XLSX.utils.book_new();

  // Create meetings sheet only if there's data
  if (meetingsData.length > 0) {
    const meetingsHeaders = getHeaders(language, accountName);
    const meetingsWorksheetData: (string | number)[][] = [];
    meetingsWorksheetData.push(meetingsHeaders);

    meetingsData.forEach(record => {
      const fields = record.fields as Record<string, unknown>;
      const transformedRow = transformRecordForExport(fields, language, accountName);
      const rowValues = meetingsHeaders.map(header => transformedRow[header]);
      meetingsWorksheetData.push(rowValues);
    });

    const meetingsWorksheet = XLSX.utils.aoa_to_sheet(meetingsWorksheetData);
    meetingsWorksheet['!cols'] = meetingsHeaders.map(header => ({
      wch: Math.max(header.length, 15)
    }));

    const meetingsSheetName = language === 'es' ? 'Reuniones' : 'Meetings';
    XLSX.utils.book_append_sheet(workbook, meetingsWorksheet, meetingsSheetName);
  }

  // Create inbox sheet only if there's data
  if (inboxData.length > 0) {
    const inboxHeaders = getInboxHeaders(language, accountName);
    const inboxWorksheetData: (string | number)[][] = [];
    inboxWorksheetData.push(inboxHeaders);

    inboxData.forEach(record => {
      const fields = record.fields as Record<string, unknown>;
      const transformedRow = transformInboxRecordForExport(fields, language, accountName);
      const rowValues = inboxHeaders.map(header => transformedRow[header]);
      inboxWorksheetData.push(rowValues);
    });

    const inboxWorksheet = XLSX.utils.aoa_to_sheet(inboxWorksheetData);
    inboxWorksheet['!cols'] = inboxHeaders.map(header => ({
      wch: Math.max(header.length, 15)
    }));

    XLSX.utils.book_append_sheet(workbook, inboxWorksheet, 'Inbox');
  }

  // Generate buffer
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });

  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}

/**
 * Trigger file download in browser
 */
export function downloadFile(
  content: Blob | string,
  filename: string,
  mimeType: string
): void {
  let blob: Blob;

  if (typeof content === 'string') {
    blob = new Blob([content], { type: mimeType });
  } else {
    blob = content;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate export filename
 */
export function generateFilename(
  accountName: string,
  startDate: string,
  endDate: string,
  format: 'csv' | 'xlsx'
): string {
  // Sanitize account name for filename
  const sanitizedAccount = accountName
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  return `${sanitizedAccount}_meetings_${startDate}_to_${endDate}.${format}`;
}

/**
 * Export data to file
 */
export function exportToFile(
  data: Record<string, unknown>[],
  accountName: string,
  startDate: string,
  endDate: string,
  format: 'csv' | 'xlsx',
  language: 'es' | 'en'
): void {
  const filename = generateFilename(accountName, startDate, endDate, format);

  if (format === 'csv') {
    const csvContent = generateCSV(data, language, accountName);
    downloadFile(csvContent, filename, 'text/csv;charset=utf-8');
  } else {
    const xlsxBlob = generateXLSX(data, language, accountName);
    downloadFile(xlsxBlob, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }
}

/**
 * Export meetings and inbox data to file (XLSX with two sheets)
 */
export function exportToFileWithInbox(
  meetingsData: Record<string, unknown>[],
  inboxData: Record<string, unknown>[],
  accountName: string,
  startDate: string,
  endDate: string,
  language: 'es' | 'en'
): void {
  const filename = generateFilename(accountName, startDate, endDate, 'xlsx');
  const xlsxBlob = generateXLSXWithInbox(meetingsData, inboxData, language, accountName);
  downloadFile(xlsxBlob, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}
